import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '@/lib/axios';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Phone, Building2, User, Calendar, Tag, MapPin, Link2, Pencil, LinkIcon, UnlinkIcon } from 'lucide-react';

interface CRMLead {
    id: number;
    trade_name: string;
    registered_name: string | null;
    contact_name: string;
    contact_phone: string;
    contact_email: string | null;
    bap_name: string | null;
    status: string | null;
    assigned_to: string | null;
    assigned_to_name: string | null;
    lead_type: string;
    program_name: string | null;
    follow_up_date: string | null;
    created_at: string | null;
    updated_at: string | null;
    primary_address_line3: string | null;
    primary_address_pincode: string | null;
    is_primary: boolean;
}

interface LeadsByPhoneModalProps {
    phone: string | null;
    userName?: string;
    currentLeadId?: number | null;
    userId?: number | null;
    isOpen: boolean;
    onClose: () => void;
    onCrmLeadChanged?: (newCrmLeadId: number | null) => void;
}

const formatDate = (iso: string | null): string => {
    if (!iso) return '-';
    try {
        return new Date(iso).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    } catch {
        return iso;
    }
};

const statusColorMap: Record<string, string> = {
    active: 'bg-green-100 text-green-700 border-green-300',
    closed: 'bg-gray-100 text-gray-600 border-gray-300',
    lost: 'bg-red-100 text-red-600 border-red-300',
    converted: 'bg-blue-100 text-blue-700 border-blue-300',
    pending: 'bg-amber-100 text-amber-700 border-amber-300',
};

function getStatusClass(status: string | null): string {
    if (!status) return 'bg-gray-100 text-gray-500 border-gray-200';
    return statusColorMap[status.toLowerCase()] ?? 'bg-purple-100 text-purple-700 border-purple-300';
}

const LeadCard: React.FC<{
    lead: CRMLead;
    currentLeadId?: number | null;
    userId?: number | null;
    settingLeadId: number | null;
    onEdit: (id: number) => void;
    onSetCrmLead: (leadId: number | null) => void;
}> = ({ lead, currentLeadId, userId, settingLeadId, onEdit, onSetCrmLead }) => {
    const isGolden = currentLeadId != null && lead.id === Number(currentLeadId);
    const isSetting = settingLeadId === lead.id;
    const isUnlinking = settingLeadId === -1 && isGolden;
    const canSetLead = userId != null;

    return (
    <div
        className={
            isGolden
                ? 'rounded-xl border-2 border-amber-400 bg-white shadow-md shadow-amber-100 hover:shadow-lg hover:border-amber-500 transition-all duration-200 overflow-hidden'
                : 'rounded-xl border border-gray-100 bg-white shadow-sm hover:shadow-md hover:border-intellend-green/30 transition-all duration-200 overflow-hidden'
        }
    >
        {/* Card Header */}
        <div
            className={
                isGolden
                    ? 'flex items-start justify-between px-4 py-3 bg-amber-50/70 border-b border-amber-200'
                    : 'flex items-start justify-between px-4 py-3 bg-gray-50/60 border-b border-gray-100'
            }
        >
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-gray-900 truncate">{lead.trade_name}</span>
                    {lead.registered_name && lead.registered_name !== lead.trade_name && (
                        <span className="text-xs text-gray-400 italic truncate">({lead.registered_name})</span>
                    )}
                    {isGolden && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-700 text-[10px] font-semibold shrink-0">
                            <Link2 className="w-2.5 h-2.5" />
                            Linked
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-[10px] font-mono text-gray-400">#{lead.id}</span>
                    {lead.bap_name && (
                        <>
                            <span className="text-gray-300">·</span>
                            <span className="text-[10px] text-intellend-green font-semibold">{lead.bap_name}</span>
                        </>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2 ml-2 shrink-0">
                {lead.status && (
                    <Badge
                        variant="outline"
                        className={`text-[10px] font-semibold ${getStatusClass(lead.status)}`}
                    >
                        {lead.status}
                    </Badge>
                )}
                {canSetLead && (
                    isGolden ? (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onSetCrmLead(null)}
                            disabled={isUnlinking}
                            className="h-6 px-2 text-[10px] font-semibold border-amber-300 text-amber-700 hover:text-red-600 hover:border-red-300 hover:bg-red-50 gap-1"
                            title="Unlink this CRM lead from the journey"
                        >
                            {isUnlinking ? (
                                <Loader2 className="w-2.5 h-2.5 animate-spin" />
                            ) : (
                                <UnlinkIcon className="w-2.5 h-2.5" />
                            )}
                            Unlink
                        </Button>
                    ) : (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onSetCrmLead(lead.id)}
                            disabled={isSetting}
                            className="h-6 px-2 text-[10px] font-semibold border-intellend-green/30 text-intellend-green hover:bg-intellend-green hover:text-white hover:border-intellend-green gap-1"
                            title="Set this as the linked CRM lead for the journey"
                        >
                            {isSetting ? (
                                <Loader2 className="w-2.5 h-2.5 animate-spin" />
                            ) : (
                                <LinkIcon className="w-2.5 h-2.5" />
                            )}
                            Set Lead
                        </Button>
                    )
                )}
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit(lead.id)}
                    className="h-6 px-2 text-[10px] font-semibold border-gray-200 text-gray-600 hover:text-intellend-green hover:border-intellend-green/50 gap-1"
                >
                    <Pencil className="w-2.5 h-2.5" />
                    Edit
                </Button>
            </div>
        </div>

        {/* Card Body */}
        <div className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {/* Contact */}
            <div className="flex items-start gap-2">
                <User className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                <div className="min-w-0">
                    <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Contact</p>
                    <p className="text-xs text-gray-700 font-semibold truncate">{lead.contact_name}</p>
                    {lead.contact_email && (
                        <p className="text-[10px] text-gray-400 truncate">{lead.contact_email}</p>
                    )}
                </div>
            </div>

            {/* Phone */}
            <div className="flex items-start gap-2">
                <Phone className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                <div className="min-w-0">
                    <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Phone</p>
                    <p className="text-xs text-gray-700 font-mono">{lead.contact_phone}</p>
                </div>
            </div>

            {/* Lead Type / Program */}
            {(lead.lead_type || lead.program_name) && (
                <div className="flex items-start gap-2">
                    <Tag className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                        <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Type / Program</p>
                        <p className="text-xs text-gray-700 truncate">
                            {[lead.lead_type, lead.program_name].filter(Boolean).join(' · ')}
                        </p>
                    </div>
                </div>
            )}

            {/* Location */}
            {(lead.primary_address_line3 || lead.primary_address_pincode) && (
                <div className="flex items-start gap-2">
                    <MapPin className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                        <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Location</p>
                        <p className="text-xs text-gray-700 truncate">
                            {[lead.primary_address_line3, lead.primary_address_pincode].filter(Boolean).join(', ')}
                        </p>
                    </div>
                </div>
            )}

            {/* Assigned To */}
            {lead.assigned_to && (
                <div className="flex items-start gap-2">
                    <Building2 className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                        <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Assigned To</p>
                        <p className="text-xs text-gray-700 truncate">{lead.assigned_to_name || lead.assigned_to}</p>
                    </div>
                </div>
            )}

            {/* Follow-up / Created */}
            <div className="flex items-start gap-2">
                <Calendar className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                <div className="min-w-0">
                    <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">
                        {lead.follow_up_date ? 'Follow-up' : 'Created'}
                    </p>
                    <p className="text-xs text-gray-700">
                        {lead.follow_up_date ? formatDate(lead.follow_up_date) : formatDate(lead.created_at)}
                    </p>
                </div>
            </div>
        </div>
    </div>
    );
};

export const LeadsByPhoneModal: React.FC<LeadsByPhoneModalProps> = ({
    phone,
    userName,
    currentLeadId,
    userId,
    isOpen,
    onClose,
    onCrmLeadChanged,
}) => {
    const [leads, setLeads] = useState<CRMLead[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // null = idle, -1 = unlinking, positive int = linking that lead id
    const [settingLeadId, setSettingLeadId] = useState<number | null>(null);
    const [setError2, setSetError] = useState<string | null>(null);
    // Track the active linked lead id locally so UI updates immediately
    const [activeCrmLeadId, setActiveCrmLeadId] = useState<number | null | undefined>(currentLeadId);
    const navigate = useNavigate();

    // Sync activeCrmLeadId when prop changes (e.g. modal reopened for different user)
    useEffect(() => {
        setActiveCrmLeadId(currentLeadId);
    }, [currentLeadId, isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        setLeads([]);
        setError(null);
        setSetError(null);
        if (!phone) return; // no phone — show empty state without API call
        setLoading(true);
        axios
            .post('/api/public/leads_by_phone/', { phone })
            .then((res) => {
                const data = res.data as CRMLead[];
                // Sort: currentLeadId-matched lead first, then is_primary leads, then by created_at desc
                const sorted = [...data].sort((a, b) => {
                    const aLinked = currentLeadId != null && a.id === currentLeadId;
                    const bLinked = currentLeadId != null && b.id === currentLeadId;
                    if (aLinked && !bLinked) return -1;
                    if (!aLinked && bLinked) return 1;
                    if (a.is_primary && !b.is_primary) return -1;
                    if (!a.is_primary && b.is_primary) return 1;
                    return 0;
                });
                setLeads(sorted);
            })
            .catch(() => setError('Failed to load leads. Please try again.'))
            .finally(() => setLoading(false));
    }, [isOpen, phone]);

    const handleEditLead = (id: number) => {
        navigate(`/admin-dashboard/crm/leads/${id}`);
        onClose();
    };

    const handleSetCrmLead = async (leadId: number | null) => {
        if (userId == null) return;
        // Use sentinel -1 for "unlinking" so we can distinguish in the UI
        setSettingLeadId(leadId === null ? -1 : leadId);
        setSetError(null);
        try {
            await axios.post('/api/public/set_bap_lead_crm_lead/', {
                user_id: userId,
                crm_lead_id: leadId,
            });
            setActiveCrmLeadId(leadId);
            if (onCrmLeadChanged) onCrmLeadChanged(leadId);
        } catch (err: unknown) {
            const msg =
                (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
                'Failed to update CRM lead link. Please try again.';
            setSetError(msg);
        } finally {
            setSettingLeadId(null);
        }
    };

    const displayPhone = phone || '-';
    const displayName = userName ? `${userName} · ` : '';

    return (
        <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0 rounded-2xl overflow-hidden">
                {/* Header */}
                <DialogHeader className="px-6 py-4 border-b bg-white shrink-0">
                    <DialogTitle className="text-base font-semibold text-gray-800 flex items-center gap-2">
                        <Phone className="w-4 h-4 text-intellend-green" />
                        <span>CRM Leads</span>
                        <span className="text-gray-400 font-normal text-sm">
                            — {displayName}
                            <span className="font-mono text-intellend-green">{displayPhone}</span>
                        </span>
                    </DialogTitle>
                    {!loading && !error && (
                        <p className="text-xs text-gray-400 mt-0.5">
                            {leads.length === 0
                                ? 'No CRM leads found for this phone number.'
                                : `${leads.length} lead${leads.length !== 1 ? 's' : ''} found`}
                            {userId != null && (
                                <span className="ml-2 text-intellend-green/70">· Use "Set Lead" to link a CRM lead to this journey</span>
                            )}
                        </p>
                    )}
                    {setError2 && (
                        <p className="text-xs text-red-500 mt-1 font-medium">{setError2}</p>
                    )}
                </DialogHeader>

                {/* Body */}
                <div className="overflow-y-auto flex-1 p-5 bg-gray-50/60">
                    {loading && (
                        <div className="flex flex-col items-center justify-center py-16 gap-3">
                            <Loader2 className="w-8 h-8 text-intellend-green animate-spin" />
                            <p className="text-sm text-gray-400">Searching leads…</p>
                        </div>
                    )}

                    {error && (
                        <div className="flex items-center justify-center py-16">
                            <p className="text-sm text-red-500">{error}</p>
                        </div>
                    )}

                    {!loading && !error && leads.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                                <Phone className="w-5 h-5 text-gray-400" />
                            </div>
                            {!phone ? (
                                <>
                                    <p className="text-sm font-medium text-gray-500">No phone number</p>
                                    <p className="text-xs text-gray-400 max-w-xs">
                                        This user does not have a phone number on record, so CRM leads cannot be looked up.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <p className="text-sm font-medium text-gray-500">No CRM leads found</p>
                                    <p className="text-xs text-gray-400 max-w-xs">
                                        There are no CRM leads associated with phone number{' '}
                                        <span className="font-mono text-gray-600">{displayPhone}</span>{' '}
                                        that you have access to.
                                    </p>
                                </>
                            )}
                        </div>
                    )}

                    {!loading && !error && leads.length > 0 && (
                        <div className="space-y-3">
                            {leads.map((lead) => (
                                <LeadCard
                                    key={lead.id}
                                    lead={lead}
                                    currentLeadId={activeCrmLeadId}
                                    userId={userId}
                                    settingLeadId={settingLeadId}
                                    onEdit={handleEditLead}
                                    onSetCrmLead={handleSetCrmLead}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default LeadsByPhoneModal;
